import { reportRules } from '../config/reportRules';
import { extractCoordinates } from './propertyUtils/coordinatesExtractor';
import { getLegalProcessSources } from './reportCalculations/legal';

function resolvePropertiesRoot(data) {
  if (!data || typeof data !== 'object') return {};

  if (data.gn_properties && typeof data.gn_properties === 'object') {
    return data.gn_properties;
  }

  if (data.properties && typeof data.properties === 'object') {
    return data.properties;
  }

  if (data.data && typeof data.data === 'object') {
    if (data.data.gn_properties && typeof data.data.gn_properties === 'object') {
      return data.data.gn_properties;
    }
    if (data.data.properties && typeof data.data.properties === 'object') {
      return data.data.properties;
    }
  }

  return {};
}

function normalizeObjectArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object');
  if (typeof value === 'object') return [value];
  return [];
}

function extractCndSituacoes(record) {
  const situacoes = [];

  const append = (value) => {
    if (value === null || value === undefined) return;
    const text = String(value).trim();
    if (text) situacoes.push(text);
  };

  if (!record || typeof record !== 'object') return situacoes;

  append(record.situacao);
  normalizeObjectArray(record.metadata).forEach((m) => append(m?.situacao));

  normalizeObjectArray(record.data).forEach((d) => {
    append(d?.situacao);
    normalizeObjectArray(d?.dados_cnd).forEach((dc) => {
      append(dc?.situacao);
      normalizeObjectArray(dc?.dados_estruturados).forEach((e) => append(e?.situacao));
      normalizeObjectArray(dc?.certidao).forEach((c) => append(c?.situacao));
      normalizeObjectArray(dc?.metadata).forEach((m) => append(m?.situacao));
    });
  });

  normalizeObjectArray(record.imovel_rural).forEach((imovel) => {
    normalizeObjectArray(imovel?.contribuinte).forEach((contribuinte) => {
      normalizeObjectArray(contribuinte?.dados_cnd).forEach((dc) => {
        append(dc?.situacao);
        normalizeObjectArray(dc?.certidao).forEach((c) => append(c?.situacao));
        normalizeObjectArray(dc?.metadata).forEach((m) => append(m?.situacao));
      });
      normalizeObjectArray(contribuinte?.metadata).forEach((m) => append(m?.situacao));
    });
  });

  return situacoes;
}

/**
 * Utilitários para cálculos de insights e scores do relatório de Due Diligence
 */

/**
 * Calcula score de risco geral (0-100, onde 0 = sem risco, 100 = risco crítico)
 */
export function calculateOverallRiskScore(data) {
  if (!data) return { score: 0, level: 'unknown', factors: [] };

  const factors = [];
  let _totalWeight = 0;
  let weightedScore = 0;

  const weightsConfig = reportRules && reportRules.risk && reportRules.risk.weights;
  const weights = {
    cadastral:
      weightsConfig && typeof weightsConfig.cadastral === 'number' ? weightsConfig.cadastral : 0.25,
    fiscal: weightsConfig && typeof weightsConfig.fiscal === 'number' ? weightsConfig.fiscal : 0.3,
    legal: weightsConfig && typeof weightsConfig.legal === 'number' ? weightsConfig.legal : 0.35,
    environmental:
      weightsConfig && typeof weightsConfig.environmental === 'number'
        ? weightsConfig.environmental
        : 0.1,
  };

  // Risco Cadastral (peso 25%)
  const cadastralRisk = calculateCadastralRisk(data);
  factors.push({ category: 'Cadastral', ...cadastralRisk, weight: weights.cadastral * 100 });
  _totalWeight += weights.cadastral * 100;
  weightedScore += cadastralRisk.score * weights.cadastral;

  // Risco Fiscal (peso 30%)
  const fiscalRisk = calculateFiscalRisk(data);
  factors.push({ category: 'Fiscal', ...fiscalRisk, weight: weights.fiscal * 100 });

  _totalWeight += weights.fiscal * 100;
  weightedScore += fiscalRisk.score * weights.fiscal;

  // Risco Jurídico (peso 35%)
  const legalRisk = calculateLegalRisk(data);
  factors.push({ category: 'Jurídico', ...legalRisk, weight: weights.legal * 100 });
  _totalWeight += weights.legal * 100;
  weightedScore += legalRisk.score * weights.legal;

  // Risco Ambiental (peso 10%)
  const environmentalRisk = calculateEnvironmentalRisk(data);
  factors.push({
    category: 'Ambiental',
    ...environmentalRisk,
    weight: weights.environmental * 100,
  });
  // eslint-disable-next-line no-useless-assignment
  _totalWeight += weights.environmental * 100;
  weightedScore += environmentalRisk.score * weights.environmental;

  const finalScore = Math.round(weightedScore);
  const level = getRiskLevel(finalScore);

  return {
    score: finalScore,
    level,
    factors: factors.sort((a, b) => b.score - a.score),
    summary: generateRiskSummary(finalScore, level, factors),
  };
}

/**
 * Risco Cadastral
 */
function calculateCadastralRisk(data) {
  let score = 0;
  const issues = [];

  const properties = resolvePropertiesRoot(data);
  const sources = Object.keys(properties);

  // Sem fontes cadastrais = risco alto
  if (sources.length === 0) {
    score += 40;
    issues.push('Nenhuma fonte cadastral encontrada');
  }

  // Verifica CAR
  const car = properties.car || [];
  if (!Array.isArray(car) || car.length === 0) {
    score += 20;
    issues.push('CAR não encontrado');
  }

  // Verifica SIGEF
  const sigef = properties.sigef || [];
  if (!Array.isArray(sigef) || sigef.length === 0) {
    score += 15;
    issues.push('SIGEF não encontrado');
  }

  // Verifica CAFIR
  const cafir = properties.cafir || [];
  if (!Array.isArray(cafir) || cafir.length === 0) {
    score += 15;
    issues.push('CAFIR não encontrado');
  }

  // Divergências de área
  const areaDivergence = calculateAreaDivergence(properties);
  if (areaDivergence.hasDivergence) {
    score += areaDivergence.severity * 10;
    issues.push(`Divergência de área: ${areaDivergence.message}`);
  }

  return {
    score: Math.min(score, 100),
    level: getRiskLevel(score),
    issues,
    details: { sources: sources.length, areaDivergence },
  };
}

/**
 * Risco Fiscal
 */
function calculateFiscalRisk(data) {
  let score = 0;
  const issues = [];

  const properties = resolvePropertiesRoot(data);

  // Verifica CCIR
  const ccir = properties.serpro_ccir || [];
  const ccirData = Array.isArray(ccir) ? ccir[0] : ccir;

  if (!ccirData) {
    score += 30;
    issues.push('CCIR não encontrado');
  } else if (ccirData.dados_ultimo_ccir) {
    const ultimoCcir = ccirData.dados_ultimo_ccir;
    const situacao = ultimoCcir.situacao_ccir || '';

    if (
      situacao.toLowerCase().includes('pendente') ||
      situacao.toLowerCase().includes('inadimplente')
    ) {
      score += 40;
      issues.push(`CCIR ${situacao}`);
    } else if (situacao.toLowerCase().includes('quitado')) {
      issues.push('CCIR quitado');
    }

    // Verifica débitos anteriores
    if (ultimoCcir.debitos_anteriores && parseFloat(ultimoCcir.debitos_anteriores) > 0) {
      score += 20;
      issues.push(`Débitos anteriores: R$ ${ultimoCcir.debitos_anteriores}`);
    }
  }

  // Verifica CND
  const cnd = properties.serpro_cnd || [];
  const cndData = Array.isArray(cnd) ? cnd[0] : cnd;

  if (!cndData) {
    score += 30;
    issues.push('CND não encontrada');
  } else {
    const situacoes = extractCndSituacoes(cndData);
    const situacao = situacoes.length > 0 ? situacoes[0] : '';
    if (situacao.toLowerCase().includes('negativa')) {
      issues.push('CND válida e negativa');
    } else if (situacao.toLowerCase().includes('positiva')) {
      score += 50;
      issues.push('CND com débitos (positiva)');
    }
  }

  // Verifica débitos IBAMA
  const ibamaDebitos = data?.ibama_debitos || properties.ibama_debitos || [];
  if (Array.isArray(ibamaDebitos) && ibamaDebitos.length > 0) {
    score += 25;
    issues.push(`${ibamaDebitos.length} débito(s) IBAMA encontrado(s)`);
  }

  return {
    score: Math.min(score, 100),
    level: getRiskLevel(score),
    issues,
    details: { ccir: !!ccirData, cnd: !!cndData, ibamaDebitos: ibamaDebitos.length },
  };
}

/**
 * Risco Jurídico
 */
function calculateLegalRisk(data) {
  let score = 0;
  const issues = [];
  const { civil, criminal } = getLegalProcessSources(data);

  const totalProcesses = civil.length + criminal.length;

  if (totalProcesses === 0) {
    issues.push('Nenhum processo encontrado');
    return { score: 0, level: 'low', issues, details: { civil: 0, criminal: 0 } };
  }

  // Processa processos cíveis
  let activeCivil = 0;
  let highValueCivil = 0;

  const legalConfig = reportRules && reportRules.legal;
  const highValueThreshold =
    legalConfig && typeof legalConfig.high_value_threshold === 'number'
      ? legalConfig.high_value_threshold
      : 100000;

  civil.forEach((proc) => {
    const status = proc.status?.inferido || proc.status?.normalizado || '';
    if (status.toLowerCase().includes('ativo') || status.toLowerCase().includes('andamento')) {
      activeCivil++;
      score += 5;
    }

    const valor = parseFloat(proc.valor_causa) || 0;
    if (valor > highValueThreshold) {
      highValueCivil++;
      score += 10;
    }
  });

  if (civil.length > 0) {
    issues.push(`${civil.length} processo(s) cível(is) - ${activeCivil} ativo(s)`);
  }

  // Processa processos criminais (peso maior)
  let activeCriminal = 0;

  criminal.forEach((proc) => {
    const status = proc.status?.inferido || proc.status?.normalizado || '';
    if (status.toLowerCase().includes('ativo') || status.toLowerCase().includes('andamento')) {
      activeCriminal++;
      score += 15;
    } else {
      score += 8;
    }

    // Gravidade por assunto
    const assunto = (proc.assunto || '').toLowerCase();
    const natureza = (proc.natureza || '').toLowerCase();

    if (assunto.includes('ambiental') || natureza.includes('ambiental')) {
      score += 10;
      issues.push('Processo criminal com tema ambiental detectado');
    }

    if (assunto.includes('fraud') || assunto.includes('estelionato')) {
      score += 12;
      issues.push('Processo criminal por fraude/estelionato detectado');
    }
  });

  if (criminal.length > 0) {
    issues.push(`${criminal.length} processo(s) criminal(is) - ${activeCriminal} ativo(s)`);
  }

  return {
    score: Math.min(score, 100),
    level: getRiskLevel(score),
    issues,
    details: {
      civil: civil.length,
      criminal: criminal.length,
      activeCivil,
      activeCriminal,
      highValueCivil,
    },
  };
}

/**
 * Risco Ambiental
 */
function calculateEnvironmentalRisk(data) {
  let score = 0;
  const issues = [];

  const properties = resolvePropertiesRoot(data);

  // Verifica embargos IBAMA
  const ibamaEmbargos = data?.ibama_embargos || properties.ibama_embargos || [];
  if (Array.isArray(ibamaEmbargos) && ibamaEmbargos.length > 0) {
    score += 60;
    issues.push(`${ibamaEmbargos.length} embargo(s) IBAMA ativo(s)`);
  }

  // Verifica regularidade ambiental
  const ibamaRegularidade = data?.ibama_regularidade || properties.ibama_regularidade || [];
  if (Array.isArray(ibamaRegularidade) && ibamaRegularidade.length === 0) {
    score += 15;
    issues.push('Regularidade ambiental não verificada');
  }

  // Verifica CAR - temas de preservação
  const car = properties.car || [];
  let hasAPP = false;
  let hasRL = false;

  if (Array.isArray(car)) {
    car.forEach((carItem) => {
      if (Array.isArray(carItem.themes)) {
        carItem.themes.forEach((theme) => {
          const nome = (theme.nome_tema || '').toUpperCase();
          if (nome.includes('APP') || nome.includes('PRESERVAÇÃO PERMANENTE')) {
            hasAPP = true;
          }
          if (nome.includes('RESERVA LEGAL') || nome.includes('LEGAL')) {
            hasRL = true;
          }
        });
      }
    });
  }

  if (!hasAPP) {
    score += 10;
    issues.push('APP não declarada no CAR');
  }

  if (!hasRL) {
    score += 10;
    issues.push('Reserva Legal não declarada no CAR');
  }

  if (issues.length === 0) {
    issues.push('Sem pendências ambientais identificadas');
  }

  return {
    score: Math.min(score, 100),
    level: getRiskLevel(score),
    issues,
    details: {
      embargos: ibamaEmbargos.length,
      hasAPP,
      hasRL,
    },
  };
}

/**
 * Calcula divergências de área entre fontes
 */
function calculateAreaDivergence(properties) {
  const areas = {};

  // Extrai áreas de cada fonte
  const car = properties.car || [];
  if (Array.isArray(car) && car.length > 0) {
    const carArea = parseFloat(car[0].area_imovel || car[0].area || 0);
    if (carArea > 0) areas.car = carArea;
  }

  const sigef = properties.sigef || [];
  if (Array.isArray(sigef) && sigef.length > 0) {
    const sigefArea = parseFloat(sigef[0].area_calculada || sigef[0].area || 0);
    if (sigefArea > 0) areas.sigef = sigefArea;
  }

  const cafir = properties.cafir || [];
  if (Array.isArray(cafir) && cafir.length > 0) {
    const cafirArea = parseFloat(cafir[0].área_da_propriedade || cafir[0].area || 0);
    if (cafirArea > 0) areas.cafir = cafirArea;
  }

  const sources = Object.keys(areas);
  if (sources.length < 2) {
    return { hasDivergence: false, severity: 0, message: 'Apenas uma fonte com área' };
  }

  const values = Object.values(areas);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const percentDiff = ((max - min) / min) * 100;

  const thresholdsConfig =
    reportRules &&
    reportRules.areas &&
    reportRules.areas.divergence &&
    reportRules.areas.divergence.thresholds;

  const smallThreshold =
    thresholdsConfig && typeof thresholdsConfig.small === 'number' ? thresholdsConfig.small : 5;
  const moderateThreshold =
    thresholdsConfig && typeof thresholdsConfig.moderate === 'number'
      ? thresholdsConfig.moderate
      : 15;
  const criticalThreshold =
    thresholdsConfig && typeof thresholdsConfig.critical === 'number'
      ? thresholdsConfig.critical
      : 30;

  let severity = 0;
  let hasDivergence = false;

  if (percentDiff > smallThreshold && percentDiff <= moderateThreshold) {
    severity = 1;
    hasDivergence = true;
  } else if (percentDiff > moderateThreshold && percentDiff <= criticalThreshold) {
    severity = 2;
    hasDivergence = true;
  } else if (percentDiff > criticalThreshold) {
    severity = 3;
    hasDivergence = true;
  }

  return {
    hasDivergence,
    severity,
    message: `${percentDiff.toFixed(1)}% entre fontes`,
    areas,
    percentDiff,
  };
}

/**
 * Determina nível de risco baseado no score
 */
function getRiskLevel(score) {
  const levelsConfig = reportRules && reportRules.risk && reportRules.risk.levels;
  if (levelsConfig) {
    const criticalMin =
      levelsConfig.critical && typeof levelsConfig.critical.min === 'number'
        ? levelsConfig.critical.min
        : 70;
    const highMin =
      levelsConfig.high && typeof levelsConfig.high.min === 'number' ? levelsConfig.high.min : 40;
    const mediumMin =
      levelsConfig.medium && typeof levelsConfig.medium.min === 'number'
        ? levelsConfig.medium.min
        : 20;
    const lowMin =
      levelsConfig.low && typeof levelsConfig.low.min === 'number' ? levelsConfig.low.min : 5;

    if (score >= criticalMin) return 'critical';
    if (score >= highMin) return 'high';
    if (score >= mediumMin) return 'medium';
    if (score >= lowMin) return 'low';
    return 'minimal';
  }
  if (score >= 70) return 'critical';
  if (score >= 40) return 'high';
  if (score >= 20) return 'medium';
  if (score >= 5) return 'low';
  return 'minimal';
}

/**
 * Gera resumo textual do risco
 */
function generateRiskSummary(score, level, factors) {
  const levelText = {
    critical: 'CRÍTICO',
    high: 'ALTO',
    medium: 'MÉDIO',
    low: 'BAIXO',
    minimal: 'MÍNIMO',
    unknown: 'DESCONHECIDO',
  };

  const topFactors = factors.filter((f) => f.score >= 20).map((f) => f.category);

  if (score >= 70) {
    return `Risco ${levelText[level]}: Recomenda-se atenção imediata. Áreas críticas: ${topFactors.join(', ') || 'múltiplas'}.`;
  }
  if (score >= 40) {
    return `Risco ${levelText[level]}: Requer análise detalhada antes de prosseguir. Focos: ${topFactors.join(', ') || 'diversos'}.`;
  }
  if (score >= 20) {
    return `Risco ${levelText[level]}: Alguns pontos de atenção identificados em ${topFactors.join(', ') || 'diferentes áreas'}.`;
  }
  if (score >= 5) {
    return `Risco ${levelText[level]}: Situação favorável com pequenas ressalvas.`;
  }
  return `Risco ${levelText[level]}: Nenhuma pendência crítica identificada.`;
}

/**
 * Calcula estatísticas cadastrais
 */
export function getCadastralStats(data) {
  const properties = resolvePropertiesRoot(data);
  const sources = Object.keys(properties);

  const stats = {
    totalSources: sources.length,
    hasCar: false,
    hasSigef: false,
    hasCafir: false,
    hasSncr: false,
    hasSerproCcir: false,
    carCount: 0,
    sigefCount: 0,
    areas: {},
    titulares: new Set(),
  };

  // CAR
  const car = properties.car || [];
  if (Array.isArray(car) && car.length > 0) {
    stats.hasCar = true;
    stats.carCount = car.length;

    car.forEach((item) => {
      const area = parseFloat(item.area_imovel || item.area || 0);
      if (area > 0) stats.areas.car = area;

      if (item.titular) stats.titulares.add(item.titular);
    });
  }

  // SIGEF
  const sigef = properties.sigef || [];
  if (Array.isArray(sigef) && sigef.length > 0) {
    stats.hasSigef = true;
    stats.sigefCount = sigef.length;

    const area = parseFloat(sigef[0].area_calculada || sigef[0].area || 0);
    if (area > 0) stats.areas.sigef = area;
  }

  // CAFIR
  const cafir = properties.cafir || [];
  if (Array.isArray(cafir) && cafir.length > 0) {
    stats.hasCafir = true;
    const area = parseFloat(cafir[0].área_da_propriedade || cafir[0].area || 0);
    if (area > 0) stats.areas.cafir = area;
  }

  // SNCR
  const sncr = properties.sncr || [];
  stats.hasSncr = Array.isArray(sncr) && sncr.length > 0;

  // SERPRO CCIR
  const ccir = properties.serpro_ccir || [];
  stats.hasSerproCcir = Array.isArray(ccir) && ccir.length > 0;

  stats.titulares = Array.from(stats.titulares);

  return stats;
}

/**
 * Calcula estatísticas fiscais
 */
export function getFiscalStats(data) {
  const properties = resolvePropertiesRoot(data);

  const stats = {
    hasCcir: false,
    ccirStatus: 'N/D',
    ccirValorTotal: 0,
    hasCnd: false,
    cndStatus: 'N/D',
    cndValidade: null,
    hasDebitos: false,
    debitosCount: 0,
    hasEmbargos: false,
    embargosCount: 0,
  };

  // CCIR
  const ccir = properties.serpro_ccir || [];
  const ccirData = Array.isArray(ccir) ? ccir[0] : ccir;

  if (ccirData?.dados_ultimo_ccir) {
    stats.hasCcir = true;
    const ultimo = ccirData.dados_ultimo_ccir;
    stats.ccirStatus = ultimo.situacao_ccir || 'N/D';
    stats.ccirValorTotal = parseFloat(ultimo.valor_total || 0);
  }

  // CND
  const cnd = properties.serpro_cnd || [];
  const cndData = Array.isArray(cnd) ? cnd[0] : cnd;

  if (cndData) {
    const situacoes = extractCndSituacoes(cndData);
    stats.hasCnd = true;
    stats.cndStatus = situacoes.length > 0 ? situacoes[0] : 'N/D';

    const validadeCandidate =
      normalizeObjectArray(cndData?.metadata).find((m) => m?.validade)?.validade ||
      normalizeObjectArray(cndData?.imovel_rural)
        .flatMap((imovel) => normalizeObjectArray(imovel?.contribuinte))
        .flatMap((contribuinte) => normalizeObjectArray(contribuinte?.metadata))
        .find((m) => m?.validade)?.validade ||
      null;

    stats.cndValidade = validadeCandidate;
  }

  // Débitos IBAMA
  const debitos = data?.ibama_debitos || properties.ibama_debitos || [];
  if (Array.isArray(debitos) && debitos.length > 0) {
    stats.hasDebitos = true;
    stats.debitosCount = debitos.length;
  }

  // Embargos IBAMA
  const embargos = data?.ibama_embargos || properties.ibama_embargos || [];
  if (Array.isArray(embargos) && embargos.length > 0) {
    stats.hasEmbargos = true;
    stats.embargosCount = embargos.length;
  }

  return stats;
}

/**
 * Calcula estatísticas jurídicas
 */
export function getLegalStats(data) {
  const { civil, criminal } = getLegalProcessSources(data);

  const stats = {
    totalProcesses: civil.length + criminal.length,
    civilCount: civil.length,
    criminalCount: criminal.length,
    activeCivil: 0,
    activeCriminal: 0,
    tribunals: new Set(),
    processTypes: {},
    valueSum: 0,
    highValueCount: 0,
  };

  const legalConfig = reportRules && reportRules.legal;
  const highValueThreshold =
    legalConfig && typeof legalConfig.high_value_threshold === 'number'
      ? legalConfig.high_value_threshold
      : 100000;

  // Processa cíveis
  civil.forEach((proc) => {
    const status = proc.status?.inferido || proc.status?.normalizado || '';
    if (status.toLowerCase().includes('ativo') || status.toLowerCase().includes('andamento')) {
      stats.activeCivil++;
    }

    if (proc.tribunal) stats.tribunals.add(proc.tribunal);

    const tipo = proc.tipo_processo || proc.classe_processual || 'Outros';
    stats.processTypes[tipo] = (stats.processTypes[tipo] || 0) + 1;

    const valor = parseFloat(proc.valor_causa) || 0;
    stats.valueSum += valor;
    if (valor > highValueThreshold) stats.highValueCount++;
  });

  // Processa criminais
  criminal.forEach((proc) => {
    const status = proc.status?.inferido || proc.status?.normalizado || '';
    if (status.toLowerCase().includes('ativo') || status.toLowerCase().includes('andamento')) {
      stats.activeCriminal++;
    }

    if (proc.tribunal) stats.tribunals.add(proc.tribunal);

    const tipo = proc.natureza || proc.classe_processual || 'Outros';
    stats.processTypes[tipo] = (stats.processTypes[tipo] || 0) + 1;
  });

  stats.tribunals = Array.from(stats.tribunals);

  return stats;
}

export function getCadastralDashboardData(data) {
  const properties = resolvePropertiesRoot(data);

  const ensureArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.filter((item) => item && typeof item === 'object');
    }
    if (typeof raw === 'object') {
      return [raw];
    }
    return [];
  };

  const carArray = ensureArray(properties.car);
  const sigefArray = ensureArray(properties.sigef);
  const sncrArray = ensureArray(properties.sncr);
  const cafirArray = ensureArray(properties.cafir);
  const ccirArray = ensureArray(properties.serpro_ccir);

  const getFirstFromArrayField = (array, field) => {
    if (!Array.isArray(array)) return null;
    for (const item of array) {
      const value = item && item[field];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return null;
  };

  const getNomePropriedade = () => {
    const nomeFromCafir = getFirstFromArrayField(cafirArray, 'nome_da_propriedade');
    if (nomeFromCafir) return nomeFromCafir;

    const nomeFromSncr = getFirstFromArrayField(sncrArray, 'nome_da_propriedade');
    if (nomeFromSncr) return nomeFromSncr;

    if (Array.isArray(sigefArray)) {
      for (const s of sigefArray) {
        const parcels = Array.isArray(s?.parcels) ? s.parcels : [];
        for (const p of parcels) {
          const nome = p?.nome_da_propriedade;
          if (nome !== null && nome !== undefined && nome !== '') {
            return nome;
          }
        }
      }
    }

    return null;
  };

  const getUf = () => {
    for (const carItem of carArray) {
      const uf = carItem?.details?.uf;
      if (uf) return uf;
    }

    const ufFromSncr = getFirstFromArrayField(sncrArray, 'uf');
    if (ufFromSncr) return ufFromSncr;

    const ufFromCafir = getFirstFromArrayField(cafirArray, 'uf');
    if (ufFromCafir) return ufFromCafir;

    const ufFromSigef = getFirstFromArrayField(sigefArray, 'uf');
    if (ufFromSigef) return ufFromSigef;

    return null;
  };

  const getMunicipio = () => {
    for (const carItem of carArray) {
      const mun = carItem?.details?.municipio;
      if (mun) return mun;
    }

    const municipioFromSncr = getFirstFromArrayField(sncrArray, 'município');
    if (municipioFromSncr) return municipioFromSncr;

    const municipioFromCafir = getFirstFromArrayField(cafirArray, 'município');
    if (municipioFromCafir) return municipioFromCafir;

    const municipioFromSigef = getFirstFromArrayField(sigefArray, 'municipio_');
    if (municipioFromSigef) return municipioFromSigef;

    return null;
  };

  const nomePropriedade = getNomePropriedade();
  const uf = getUf();
  const municipio = getMunicipio();

  let lat = null;
  let lng = null;
  const coords = extractCoordinates(data);
  if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
    lat = coords.lat;
    lng = coords.lng;
  }

  let pfCount = 0;
  let pjCount = 0;

  ccirArray.forEach((ccir) => {
    const titulares = Array.isArray(ccir?.titulares) ? ccir.titulares : [];
    titulares.forEach((tit) => {
      const raw = tit?.cpf_cnpj;
      if (!raw) return;
      const digits = String(raw).replace(/\D/g, '');
      if (digits.length === 11) {
        pfCount += 1;
      } else if (digits.length === 14) {
        pjCount += 1;
      }
    });
  });

  const extractBiomasForDashboard = (sobreposicaoData) => {
    if (!sobreposicaoData) return [];

    const array = Array.isArray(sobreposicaoData) ? sobreposicaoData : [sobreposicaoData];

    const collected = [];

    array.forEach((item) => {
      const biomas = Array.isArray(item?.temas?.biomas) ? item.temas.biomas : [];
      biomas.forEach((b) => {
        const nome = b?.bioma;
        const pct = b?.pct_imovel;
        if (nome) {
          collected.push({ bioma: nome, pct });
        }
      });
    });

    const map = new Map();
    collected.forEach((b) => {
      const key = `${b.bioma}-${b.pct ?? ''}`;
      if (!map.has(key)) {
        map.set(key, b);
      }
    });

    return Array.from(map.values()).slice(0, 3);
  };

  const biomas = extractBiomasForDashboard(properties.sobreposicao);

  const computeCarSourceStatus = () => {
    const label = 'CAR';
    const count = carArray.length;

    if (count === 0) {
      return { label, color: 'error', count };
    }

    const statusCodes = [];
    carArray.forEach((carItem) => {
      const code = carItem?.details?.situacao_do_cadastro;
      if (code !== null && code !== undefined && code !== '') {
        statusCodes.push(String(code).toUpperCase());
      }
    });

    const unique = Array.from(new Set(statusCodes));

    if (unique.length > 1) {
      return { label, color: 'warning', count };
    }

    let color = 'default';
    const code = unique[0];
    if (!code) {
      color = 'default';
    } else if (code === 'AT') {
      color = 'success';
    } else if (code === 'PE') {
      color = 'warning';
    } else if (code === 'CA') {
      color = 'error';
    }

    return { label, color, count };
  };

  const computeSigefSourceStatus = () => {
    const label = 'SIGEF';
    const count = sigefArray.length;

    if (count === 0) {
      return { label, color: 'error', count, parcelas: 0 };
    }

    let parcelas = 0;
    let anyCertificada = false;

    sigefArray.forEach((sigefItem) => {
      const parcels = Array.isArray(sigefItem?.parcels) ? sigefItem.parcels : [];
      parcels.forEach((p) => {
        parcelas += 1;
        const sitRaw =
          p?.['situação'] !== undefined
            ? p['situação']
            : p?.['situacao'] !== undefined
              ? p['situacao']
              : p?.situacao;
        const sitNorm = sitRaw ? String(sitRaw).toLowerCase() : '';
        if (sitNorm.includes('certific')) {
          anyCertificada = true;
        }
      });
    });

    const color = anyCertificada ? 'success' : 'warning';

    return { label, color, count, parcelas };
  };

  const computeSncrSourceStatus = () => {
    const label = 'SNCR';
    let count = 0;

    sncrArray.forEach((sncrItem) => {
      const codigo = sncrItem?.codigo_incra;
      if (codigo !== null && codigo !== undefined && codigo !== '') {
        count += 1;
      }
    });

    const color = count > 0 ? 'success' : 'error';
    return { label, color, count };
  };

  const computeCafirSourceStatus = () => {
    const label = 'CAFIR';
    let count = 0;

    cafirArray.forEach((cafirItem) => {
      const codigo = cafirItem?.codigo_incra;
      if (codigo !== null && codigo !== undefined && codigo !== '') {
        count += 1;
      }
    });

    const color = count > 0 ? 'success' : 'error';
    return { label, color, count };
  };

  const carSource = computeCarSourceStatus();
  const sigefSource = computeSigefSourceStatus();
  const sncrSource = computeSncrSourceStatus();
  const cafirSource = computeCafirSourceStatus();

  return {
    nomePropriedade: nomePropriedade || null,
    uf: uf || null,
    municipio: municipio || null,
    lat,
    lng,
    pfCount,
    pjCount,
    biomas,
    car: carSource,
    sigef: sigefSource,
    sncr: sncrSource,
    cafir: cafirSource,
  };
}

export {
  getEnvironmentalStats,
  classifyReservaLegalStatus,
  getCarReservaLegalDashboardStatus,
  getPraDashboardStatus,
  getEmbargosDashboardStatus,
} from './reportCalculations/environmental';

export {
  getCcirsDashboardStatus,
  getCndDashboardStatus,
  getIbamaCertidaoStatusFromDocs,
} from './reportCalculations/certidoes';

export { getSobreposicoesDashboardStatus } from './reportCalculations/sobreposicoes';

export { getAreasRegistradasDashboardData } from './reportCalculations/areasRegistradas';

export { getLegalDashboardData } from './reportCalculations/legal';
